import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Gift } from "lucide-react";
import { WA_NUMBER_DISPLAY, waLink, DEFAULT_WA_MSG } from "../data/catalogue";
import { WhatsAppIcon, scrollToSection, EASE } from "./Shared";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: 0 },
  transition: { duration: 1.05, delay: 0.25 + i * 0.15, ease: EASE },
});

const fadeAnim = (d) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease: EASE },
});

const HeroImg = ({ src, alt, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92, y: 30 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 1.1, delay, ease: EASE }}
    className={`overflow-hidden shadow-[0_30px_60px_-20px_rgba(43,33,28,0.25)] ${className}`}
  >
    <img src={src} alt={alt} className="h-full w-full object-cover" loading="eager" />
  </motion.div>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const ySlow = useTransform(scrollY, [0, 600], [0, 60]);
  const yFast = useTransform(scrollY, [0, 600], [0, -40]);
  const yChip = useTransform(scrollY, [0, 600], [0, -90]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      {/* soft peach wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-15%] h-[720px] w-[720px] rounded-full opacity-70"
        style={{ background: "radial-gradient(circle, #FCEADE 0%, rgba(252,234,222,0) 68%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 left-[-20%] h-[560px] w-[560px] rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, #D8E4DC 0%, rgba(216,228,220,0) 70%)" }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-10 sm:pt-16 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* copy */}
        <div className="lg:col-span-7">
          <motion.div {...fadeAnim(0.1)}>
            <span className="inline-flex items-center gap-2 rounded-full border border-sand bg-peach/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-coral">
              <Gift className="h-3.5 w-3.5" />
              Customised Gifting Studio
            </span>
          </motion.div>

          <h1 className="mt-7 font-serif font-medium text-ink leading-[1.02] tracking-tight text-[clamp(3rem,9vw,5.75rem)]">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>
                Elevating
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block flex items-center gap-4" {...lineAnim(1)}>
                <span className="italic text-coral">every</span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                  className="hidden sm:inline-block origin-left h-[0.62em] w-[1.7em] rounded-full overflow-hidden"
                >
                  <img
                    src="/products/giftsets_p03.jpg"
                    alt="Gift set from the Anubandh catalogue"
                    className="h-full w-full object-cover"
                  />
                </motion.span>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" {...lineAnim(2)}>
                Expression<span className="text-sage">.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p {...fadeAnim(0.75)} className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-cocoa">
            Bags, desktop gifts, divine idols, table calendars and curated gift sets, personalised with your
            logo and packed with intention. Screenshot what you love, share it with us on
            WhatsApp, and we&apos;ll take it from there.
          </motion.p>

          <motion.div {...fadeAnim(0.9)} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-explore-collections-btn"
              onClick={() => scrollToSection("#collections")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold text-cream hover:bg-coral-deep transition-colors duration-300"
            >
              Explore the Collections
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2.5 rounded-full border border-sand bg-white/70 px-7 py-3.5 text-sm font-semibold text-ink hover:border-sage hover:text-sage-deep transition-colors duration-300"
            >
              <WhatsAppIcon className="h-4 w-4 text-sage-deep" />
              {WA_NUMBER_DISPLAY}
            </a>
          </motion.div>

          <motion.p {...fadeAnim(1.05)} className="mt-6 text-xs sm:text-sm text-mocha">
            Customised pieces from <span className="font-semibold text-ink">35 pcs</span>
            <span className="mx-2 text-sand">·</span>
            Ready-made pieces in <span className="font-semibold text-ink">any quantity</span>
          </motion.p>
        </div>

        {/* parallax cluster */}
        <div className="relative hidden lg:block lg:col-span-5 h-[560px]" data-testid="hero-visual-cluster">
          <motion.div style={{ y: ySlow }} className="absolute right-0 top-2">
            <HeroImg
              src="/products/bags_p04.jpg"
              alt="Vegan leather backpack from the Anubandh Bag Set catalogue"
              className="w-[300px] h-[440px] rounded-t-full rounded-b-[2rem]"
              delay={0.5}
            />
          </motion.div>
          <motion.div style={{ y: yFast }} className="absolute left-4 bottom-6">
            <HeroImg
              src="/products/premium_p10.jpg"
              alt="Premium rigid gift box from the Anubandh Premium Gift Sets catalogue"
              className="w-[220px] h-[220px] rounded-[2rem]"
              delay={0.7}
            />
          </motion.div>
          <motion.div style={{ y: ySlow }} className="absolute left-0 top-6">
            <HeroImg
              src="/products/divine_p02.jpg"
              alt="Lord Ganesha idol from the Anubandh Divine catalogue"
              className="w-[110px] h-[110px] rounded-full border-4 border-peach"
              delay={0.9}
            />
          </motion.div>

          <motion.a
            style={{ y: yChip }}
            href={waLink(DEFAULT_WA_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-screenshot-chip"
            className="floaty absolute right-2 bottom-14 inline-flex items-center gap-2.5 rounded-2xl border border-sand bg-cream/90 backdrop-blur-md px-5 py-3.5 shadow-[0_20px_45px_-18px_rgba(43,33,28,0.3)]"
          >
            <span className="grid place-items-center h-8 w-8 rounded-full bg-sage-light text-sage-deep">
              <WhatsAppIcon className="h-4 w-4" />
            </span>
            <span className="text-xs font-semibold leading-tight text-ink">
              Screenshot &amp; share
              <span className="block font-normal text-cocoa">get prices on WhatsApp</span>
            </span>
          </motion.a>

          <motion.div
            {...fadeAnim(1.1)}
            className="absolute -left-6 bottom-0 h-[104px] w-[104px]"
          >
            <div className="relative h-full w-full">
              <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
                <defs>
                  <path id="badge-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                </defs>
                <text className="fill-cocoa" style={{ fontSize: "9.2px", letterSpacing: "2.6px", fontWeight: 600 }}>
                  <textPath href="#badge-circle">
                    ANUBANDH · ELEVATING EVERY EXPRESSION ·
                  </textPath>
                </text>
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <img
                  src="/anubandh-logo.jpg"
                  alt="Anubandh logo"
                  className="h-12 w-12 rounded-full object-cover border-2 border-peach"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* mobile image row */}
        <div className="grid grid-cols-3 gap-3 lg:hidden" data-testid="hero-mobile-gallery">
          {[
            { src: "/products/bags_p04.jpg", alt: "Vegan leather backpack" },
            { src: "/products/premium_p10.jpg", alt: "Premium gift box" },
            { src: "/products/divine_p02.jpg", alt: "Lord Ganesha idol" },
          ].map((im, i) => (
            <motion.div
              key={im.src}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 + i * 0.12, ease: EASE }}
              className={`overflow-hidden rounded-3xl shadow-lg ${i === 0 ? "aspect-[3/4]" : "aspect-square self-end"}`}
            >
              <img src={im.src} alt={im.alt} className="h-full w-full object-cover" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
